import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
  Dimensions,
  useWindowDimensions,
} from 'react-native';
import { X, Plus, Trash2, Camera, User } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';
import { isWeb } from '@/lib/platform';
import { KeyboardAwareBody } from '@/components/modal/KeyboardAwareBody';
import AnimatedPressable from '@/components/AnimatedPressable';

interface ProfileEditModalProps {
  visible: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  title: string;
  section: string;
  initialData: any;
}

// Section complexity for determining modal size
const SECTION_SIZE: Record<string, 'small' | 'medium' | 'large'> = {
  // Investor sections
  user: 'medium',
  basic: 'small',
  ticketSize: 'small',
  bio: 'medium',
  social: 'medium',
  valueAdd: 'large',
  thesis: 'large',
  // Startup sections
  companyBasic: 'large',
  fundraising: 'medium',
  business: 'large',
  traction: 'medium',
  market: 'medium',
  founderBackground: 'medium',
};

export default function ProfileEditModal({
  visible,
  onClose,
  onSave,
  title,
  section,
  initialData,
}: ProfileEditModalProps) {
  const colors = useThemeColors();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const [formData, setFormData] = useState<any>(initialData || {});

  // Calculate modal height based on section size
  const sectionSize = SECTION_SIZE[section] || 'medium';
  const modalConfig = useMemo(() => {
    if (isWeb) {
      // Web: centered modal with max dimensions
      const maxWidth = Math.min(480, windowWidth - 48);
      const maxHeight = sectionSize === 'small'
        ? Math.min(350, windowHeight * 0.5)
        : sectionSize === 'medium'
        ? Math.min(480, windowHeight * 0.65)
        : Math.min(640, windowHeight * 0.8);
      return { maxWidth, maxHeight, presentationStyle: 'overFullScreen' as const };
    } else {
      // Mobile: formSheet for small, pageSheet for larger
      return {
        presentationStyle: sectionSize === 'small' ? 'formSheet' as const : 'pageSheet' as const,
        maxHeight: sectionSize === 'small'
          ? windowHeight * 0.45
          : sectionSize === 'medium'
          ? windowHeight * 0.6
          : windowHeight * 0.85,
      };
    }
  }, [sectionSize, windowHeight, windowWidth]);

  useEffect(() => {
    setFormData(initialData || {});
  }, [initialData, visible]);

  const handleSave = () => {
    onSave(formData);
    onClose();
  };

  const updateField = (field: string, value: any) => {
    setFormData((prev: any) => ({ ...prev, [field]: value }));
  };

  const updateArrayField = (field: string, index: number, value: string) => {
    const arr = [...(formData[field] || [])];
    arr[index] = value;
    setFormData((prev: any) => ({ ...prev, [field]: arr }));
  };

  const addArrayItem = (field: string) => {
    const arr = [...(formData[field] || []), ''];
    setFormData((prev: any) => ({ ...prev, [field]: arr }));
  };

  const removeArrayItem = (field: string, index: number) => {
    const arr = (formData[field] || []).filter((_: any, i: number) => i !== index);
    setFormData((prev: any) => ({ ...prev, [field]: arr }));
  };

  /**
   * The three helpers above are hard-wired to `string[]`: `addArrayItem` pushes
   * a bare `''` and `updateArrayField` writes a string at an index. Previous
   * roles are `{ title, company, years }[]` in a jsonb column, so they need
   * their own pair — which is why the Experience list was read-only and the
   * only thing that could ever write it was LinkedIn enrichment.
   */
  const updateObjectArrayField = (field: string, index: number, key: string, value: string) => {
    const arr = [...(formData[field] || [])];
    arr[index] = { ...(arr[index] || {}), [key]: value };
    setFormData((prev: any) => ({ ...prev, [field]: arr }));
  };

  const addObjectArrayItem = (field: string, blank: Record<string, string>) => {
    const arr = [...(formData[field] || []), { ...blank }];
    setFormData((prev: any) => ({ ...prev, [field]: arr }));
  };

  // Compact vs standard spacing based on section size
  const isCompact = sectionSize === 'small';

  const renderInput = (
    label: string,
    field: string,
    placeholder: string,
    multiline = false
  ) => (
    <View className={isCompact ? 'mb-3' : 'mb-4'}>
      <Text
        style={{ color: colors.text.secondary }}
        className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1.5`}
      >
        {label}
      </Text>
      <TextInput
        value={formData[field] || ''}
        onChangeText={(v) => updateField(field, v)}
        placeholder={placeholder}
        placeholderTextColor={colors.text.tertiary}
        multiline={multiline}
        numberOfLines={multiline ? 3 : 1}
        className={`rounded-xl px-3 ${multiline ? 'py-2.5 min-h-[80px]' : isCompact ? 'h-10' : 'h-11'}`}
        style={{
          backgroundColor: colors.surface.secondary,
          color: colors.text.primary,
          textAlignVertical: multiline ? 'top' : 'center',
          fontSize: isCompact ? 14 : 15,
        }}
      />
    </View>
  );

  /**
   * An array of objects, each row a small group of labelled sub-fields.
   * Used for `previousRoles`; `subFields` keeps it general rather than
   * hardcoding that one shape.
   */
  const renderObjectArrayInput = (
    label: string,
    field: string,
    subFields: { key: string; placeholder: string; flex?: number }[],
    addLabel: string,
  ) => (
    <View className={isCompact ? 'mb-3' : 'mb-4'}>
      <Text
        style={{ color: colors.text.secondary }}
        className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1.5`}
      >
        {label}
      </Text>
      {(formData[field] || []).map((item: Record<string, string>, index: number) => (
        <View
          key={index}
          className="rounded-xl p-2.5 mb-2"
          style={{ backgroundColor: colors.surface.secondary }}
        >
          <View className="flex-row items-center gap-2">
            <View className="flex-1">
              {subFields.map((sub) => (
                <TextInput
                  key={sub.key}
                  value={item?.[sub.key] ?? ''}
                  onChangeText={(v) => updateObjectArrayField(field, index, sub.key, v)}
                  placeholder={sub.placeholder}
                  placeholderTextColor={colors.text.tertiary}
                  className={`rounded-lg px-3 mb-1.5 ${isCompact ? 'h-9' : 'h-10'}`}
                  style={{
                    backgroundColor: colors.surface.primary,
                    color: colors.text.primary,
                    fontSize: isCompact ? 14 : 15,
                  }}
                />
              ))}
            </View>
            <AnimatedPressable
              onPress={() => removeArrayItem(field, index)}
              accessibilityRole="button"
              accessibilityLabel={`Remove ${addLabel.toLowerCase()} ${index + 1}`}
              className={`${isCompact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg items-center justify-center`}
              style={{ backgroundColor: colors.red.light }}
            >
              <Trash2 size={isCompact ? 14 : 16} color={colors.error} />
            </AnimatedPressable>
          </View>
        </View>
      ))}
      <AnimatedPressable
        onPress={() =>
          addObjectArrayItem(
            field,
            Object.fromEntries(subFields.map((sub) => [sub.key, ''])),
          )
        }
        accessibilityRole="button"
        accessibilityLabel={`Add ${addLabel.toLowerCase()}`}
        className="flex-row items-center gap-1.5 py-1.5"
      >
        <Plus size={isCompact ? 14 : 16} color={colors.primary[600]} />
        <Text style={{ color: colors.primary[600] }} className={`${isCompact ? 'text-sm' : ''} font-medium`}>
          Add {addLabel}
        </Text>
      </AnimatedPressable>
    </View>
  );

  const renderArrayInput = (label: string, field: string, placeholder: string) => (
    <View className={isCompact ? 'mb-3' : 'mb-4'}>
      <Text
        style={{ color: colors.text.secondary }}
        className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1.5`}
      >
        {label}
      </Text>
      {(formData[field] || []).map((item: string, index: number) => (
        <View key={index} className="flex-row items-center gap-2 mb-2">
          <TextInput
            value={item}
            onChangeText={(v) => updateArrayField(field, index, v)}
            placeholder={placeholder}
            placeholderTextColor={colors.text.tertiary}
            className={`flex-1 rounded-xl px-3 ${isCompact ? 'h-10' : 'h-11'}`}
            style={{
              backgroundColor: colors.surface.secondary,
              color: colors.text.primary,
              fontSize: isCompact ? 14 : 15,
            }}
          />
          <AnimatedPressable
            onPress={() => removeArrayItem(field, index)}
            className={`${isCompact ? 'w-8 h-8' : 'w-9 h-9'} rounded-lg items-center justify-center`}
            style={{ backgroundColor: colors.red.light }}
          >
            <Trash2 size={isCompact ? 14 : 16} color={colors.error} />
          </AnimatedPressable>
        </View>
      ))}
      <AnimatedPressable
        onPress={() => addArrayItem(field)}
        className="flex-row items-center gap-1.5 py-1.5"
      >
        <Plus size={isCompact ? 14 : 16} color={colors.primary[600]} />
        <Text style={{ color: colors.primary[600] }} className={`${isCompact ? 'text-sm' : ''} font-medium`}>
          Add {label.replace(/s$/, '')}
        </Text>
      </AnimatedPressable>
    </View>
  );

  const renderSectionContent = () => {
    switch (section) {
      case 'user':
        return (
          <>
            {/* Profile Photo Placeholder */}
            <View className="items-center mb-5">
              <View
                className="w-24 h-24 rounded-full items-center justify-center mb-3"
                style={{ backgroundColor: colors.primary[100] }}
              >
                {formData.name ? (
                  <Text style={{ color: colors.primary[600] }} className="text-3xl font-bold">
                    {formData.name.split(' ').map((n: string) => n[0]).join('').toUpperCase()}
                  </Text>
                ) : (
                  <User size={40} color={colors.primary[600]} />
                )}
              </View>
              <AnimatedPressable
                onPress={() => {
                  // Photo upload would be handled here
                }}
                className="flex-row items-center gap-2 px-4 py-2 rounded-full"
                style={{ backgroundColor: colors.primary[50] }}
              >
                <Camera size={16} color={colors.primary[600]} />
                <Text style={{ color: colors.primary[600] }} className="font-medium text-sm">
                  Change Photo
                </Text>
              </AnimatedPressable>
            </View>
            {renderInput('Full Name', 'name', 'e.g., Sarah Chen')}
            {/* `Email` and `Role` used to be edited here and went nowhere.
                Email is owned by the auth provider — changing it is an auth
                flow, not a profile patch. `Role` was worse: in the store it
                meant a job title, but the column of that name
                (`profiles.role`) is the authorization trust boundary
                (startup | investor | admin), trigger-guarded precisely because
                a client-writable path into it is privilege escalation. The
                job title a founder actually reads is `headline`, which the
                Bio section owns. See src/lib/investor/profile-edit-fields.ts. */}
          </>
        );

      case 'basic':
        return (
          <>
            {renderInput('Firm Name', 'firm', 'e.g., Horizon Ventures')}
            {renderInput('Location', 'location', 'e.g., San Francisco, CA')}
            {renderInput('Years Investing', 'yearsInvesting', 'e.g., 10')}
          </>
        );

      case 'bio':
        return (
          <>
            {renderInput('Headline', 'headline', 'e.g., Former Stripe PM | Angel Investor')}
            {renderInput('Bio', 'bio', 'Tell founders about your background...', true)}
            {renderObjectArrayInput(
              'Experience',
              'previousRoles',
              [
                { key: 'title', placeholder: 'Role, e.g. VP Product' },
                { key: 'company', placeholder: 'Company' },
                { key: 'years', placeholder: 'Years, e.g. 2016-2022' },
              ],
              'Role',
            )}
          </>
        );

      case 'thesis':
        return (
          <>
            {renderInput('Investment Philosophy', 'thesisStatement', 'What drives your investment decisions?', true)}
            {renderInput('Sweet Spot', 'sweetSpot', 'e.g., B2B SaaS at seed stage')}
            {renderArrayInput('Focus Sectors', 'sectors', 'e.g., Enterprise SaaS')}
            {renderArrayInput('Stage Preferences', 'stagePreference', 'e.g., Seed')}
            {renderArrayInput('Team Preferences', 'teamPreferences', 'e.g., Technical founders')}
            {renderArrayInput('Deal Breakers', 'dealBreakers', 'e.g., No hardware')}
          </>
        );

      case 'ticketSize':
        return (
          <>
            {renderInput('Minimum Check Size ($)', 'min', 'e.g., 50000')}
            {renderInput('Maximum Check Size ($)', 'max', 'e.g., 500000')}
          </>
        );

      case 'valueAdd':
        return (
          <>
            {renderArrayInput('Expertise Areas', 'expertiseAreas', 'e.g., Go-to-market strategy')}
            {renderInput('How You Help', 'description', 'Describe how you support portfolio companies...', true)}
            <View className="flex-row items-center justify-between py-3">
              <Text style={{ color: colors.text.secondary }} className="font-medium">
                Offer Office Hours
              </Text>
              <AnimatedPressable
                onPress={() => updateField('officeHours', !formData.officeHours)}
                className="w-12 h-7 rounded-full justify-center"
                style={{
                  backgroundColor: formData.officeHours
                    ? colors.primary[600]
                    : colors.light[400],
                }}
              >
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{
                    marginLeft: formData.officeHours ? 24 : 4,
                  }}
                />
              </AnimatedPressable>
            </View>
            {renderInput('Board Seats Held', 'boardExperience', 'e.g., 5')}
          </>
        );

      case 'social':
        return (
          <>
            {renderInput('Twitter Handle', 'twitter', 'e.g., @yourhandle')}
            {renderInput('Website', 'website', 'e.g., yoursite.com')}
            {renderInput('Calendly Link', 'calendly', 'e.g., calendly.com/you')}
            {renderInput('Substack/Newsletter', 'substack', 'e.g., you.substack.com')}
          </>
        );

      // ========================
      // STARTUP SECTIONS
      // ========================

      case 'companyBasic':
        return (
          <>
            {renderInput('Company Name', 'name', 'e.g., TechFlow AI')}
            {renderInput('Tagline', 'tagline', 'e.g., AI-powered automation for SMBs')}
            {renderInput('Industry', 'industry', 'e.g., Enterprise SaaS')}
            <View className={isCompact ? 'mb-3' : 'mb-4'}>
              <Text
                style={{ color: colors.text.secondary }}
                className={`${isCompact ? 'text-xs' : 'text-sm'} font-medium mb-1.5`}
              >
                Stage
              </Text>
              <View className="flex-row gap-2">
                {['pre-seed', 'seed', 'series-a'].map((stage) => (
                  <AnimatedPressable
                    key={stage}
                    onPress={() => updateField('stage', stage)}
                    className="flex-1 py-3 rounded-xl items-center"
                    style={{
                      backgroundColor: formData.stage === stage
                        ? colors.primary[600]
                        : colors.surface.secondary,
                    }}
                  >
                    <Text
                      style={{
                        color: formData.stage === stage ? 'white' : colors.text.primary,
                      }}
                      className="font-medium text-sm"
                    >
                      {stage === 'pre-seed' ? 'Pre-Seed' : stage === 'seed' ? 'Seed' : 'Series A'}
                    </Text>
                  </AnimatedPressable>
                ))}
              </View>
            </View>
            {renderInput('Location', 'location', 'e.g., San Francisco, CA')}
            {renderInput('Founded Year', 'foundedYear', 'e.g., 2022')}
            {renderInput('Website', 'website', 'e.g., techflow.ai')}
            {renderInput('LinkedIn', 'linkedIn', 'e.g., linkedin.com/company/techflow')}
          </>
        );

      case 'fundraising':
        return (
          <>
            {renderInput('Funding Target ($)', 'fundingTarget', 'e.g., 2000000')}
            {renderInput('Minimum Ticket ($)', 'minimumTicket', 'e.g., 25000')}
            <View className="flex-row items-center justify-between py-3">
              <Text style={{ color: colors.text.secondary }} className="font-medium">
                Syndicates Allowed
              </Text>
              <AnimatedPressable
                onPress={() => updateField('syndicatesAllowed', !formData.syndicatesAllowed)}
                className="w-12 h-7 rounded-full justify-center"
                style={{
                  backgroundColor: formData.syndicatesAllowed
                    ? colors.primary[600]
                    : colors.light[400],
                }}
              >
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{
                    marginLeft: formData.syndicatesAllowed ? 24 : 4,
                  }}
                />
              </AnimatedPressable>
            </View>
            <View className="flex-row items-center justify-between py-3">
              <Text style={{ color: colors.text.secondary }} className="font-medium">
                Actively Raising
              </Text>
              <AnimatedPressable
                onPress={() => updateField('isActivelyRaising', !formData.isActivelyRaising)}
                className="w-12 h-7 rounded-full justify-center"
                style={{
                  backgroundColor: formData.isActivelyRaising
                    ? colors.traffic.green
                    : colors.light[400],
                }}
              >
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{
                    marginLeft: formData.isActivelyRaising ? 24 : 4,
                  }}
                />
              </AnimatedPressable>
            </View>
          </>
        );

      case 'business':
        return (
          <>
            {renderInput('Problem', 'problem', 'What problem are you solving?', true)}
            {renderInput('Solution', 'solution', 'How does your product solve this?', true)}
            {renderInput('Target Customer', 'targetCustomer', 'Who is your ideal customer?', true)}
          </>
        );

      case 'traction':
        return (
          <>
            {renderInput('Team Size', 'teamSize', 'e.g., 8')}
            {renderInput('Monthly Revenue ($)', 'monthlyRevenue', 'e.g., 75000')}
            {renderInput('User Count', 'userCount', 'e.g., 1200')}
            {renderInput('Growth Rate (%)', 'growthRate', 'e.g., 15')}
          </>
        );

      case 'market':
        return (
          <>
            {renderInput('Market Size ($)', 'marketSize', 'e.g., 5200000000')}
            {renderArrayInput('Competitors', 'competitors', 'e.g., Competitor name')}
          </>
        );

      case 'founderBackground':
        return (
          <>
            <View className="flex-row items-center justify-between py-3 mb-3">
              <View className="flex-1 mr-4">
                <Text style={{ color: colors.text.primary }} className="font-medium">
                  Serial Founder
                </Text>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5">
                  Have you founded companies before?
                </Text>
              </View>
              <AnimatedPressable
                onPress={() => updateField('isSerialFounder', !formData.isSerialFounder)}
                className="w-12 h-7 rounded-full justify-center"
                style={{
                  backgroundColor: formData.isSerialFounder
                    ? colors.primary[600]
                    : colors.light[400],
                }}
              >
                <View
                  className="w-5 h-5 rounded-full bg-white"
                  style={{
                    marginLeft: formData.isSerialFounder ? 24 : 4,
                  }}
                />
              </AnimatedPressable>
            </View>
            {formData.isSerialFounder && renderArrayInput('Previous Ventures', 'previousVentures', 'e.g., Company name (outcome)')}
          </>
        );

      default:
        return (
          <Text style={{ color: colors.text.tertiary }}>
            No editable fields for this section.
          </Text>
        );
    }
  };

  // Web modal - centered overlay
  if (isWeb) {
    return (
      <Modal
        visible={visible}
        animationType="fade"
        transparent
        onRequestClose={onClose}
      >
        <Pressable
          className="flex-1 items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
          onPress={onClose}
        >
          <Pressable
            onPress={(e) => e.stopPropagation()}
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: colors.surface.primary,
              width: modalConfig.maxWidth,
              maxHeight: modalConfig.maxHeight,
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.25,
              shadowRadius: 24,
            }}
          >
            {/* Header */}
            <View
              className="flex-row items-center justify-between px-5 py-4 border-b"
              style={{ borderColor: colors.border.light }}
            >
              <AnimatedPressable onPress={onClose} className="w-8 h-8 items-center justify-center rounded-lg" style={{ backgroundColor: colors.surface.secondary }}>
                <X size={18} color={colors.text.secondary} />
              </AnimatedPressable>
              <Text style={{ color: colors.text.primary }} className="text-base font-bold">
                {title}
              </Text>
              <AnimatedPressable
                onPress={handleSave}
                className="px-4 py-2 rounded-lg"
                style={{ backgroundColor: colors.primary[600] }}
              >
                <Text className="text-white font-semibold text-sm">Save</Text>
              </AnimatedPressable>
            </View>

            {/* Content */}
            <ScrollView
              className="px-5 py-4"
              showsVerticalScrollIndicator={false}
              style={{ maxHeight: (modalConfig.maxHeight || 400) - 72 }}
            >
              {renderSectionContent()}
              <View className="h-4" />
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>
    );
  }

  // Mobile modal - native sheet presentation
  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle={modalConfig.presentationStyle || 'pageSheet'}
      onRequestClose={onClose}
    >
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        className="flex-1"
        style={{
          backgroundColor: colors.surface.primary,
          maxHeight: modalConfig.maxHeight,
        }}
      >
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-5 py-3 border-b"
          style={{ borderColor: colors.border.light }}
        >
          <AnimatedPressable onPress={onClose} className="w-10 h-10 items-center justify-center">
            <X size={22} color={colors.text.secondary} />
          </AnimatedPressable>
          <Text style={{ color: colors.text.primary }} className="text-base font-bold">
            {title}
          </Text>
          <AnimatedPressable
            onPress={handleSave}
            className="px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary[600] }}
          >
            <Text className="text-white font-semibold text-sm">Save</Text>
          </AnimatedPressable>
        </View>

        {/* Content */}
        {/* Keyboard-aware: this modal is a full-height sheet whose fields run
            past the fold (the thesis section alone has six), so the lower ones
            sat behind the keyboard. Classes unchanged — the layout is
            identical, only the scroll container differs. */}
        <KeyboardAwareBody className="flex-1 px-5 py-4">
          {renderSectionContent()}
          <View className="h-6" />
        </KeyboardAwareBody>
      </KeyboardAvoidingView>
    </Modal>
  );
}
